import {
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
  type RefObject,
  type ReactNode,
} from 'react';

export type PopoverContextType = {
  close: () => void;
  id: string;
  isOpen: boolean;
  open: () => void;
  popoverRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLButtonElement | null>;
};

export const PopoverContext = createContext<PopoverContextType | undefined>(
  undefined,
);

export type PopoverRootProps = {
  children: ReactNode;
  onClose?: () => void;
  onOpen?: () => void;
};

export const PopoverRoot = (props: PopoverRootProps) => {
  const { children, onClose, onOpen } = props;
  const id = useId();
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
    if (onOpen) {
      onOpen();
    }
  }, [onOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  return (
    <PopoverContext.Provider
      value={{ close, id, isOpen, open, popoverRef, triggerRef }}
    >
      {children}
    </PopoverContext.Provider>
  );
};

export const usePopover = (): PopoverContextType => {
  const context = useContext(PopoverContext);

  if (context === undefined) {
    throw new Error('usePopover must be used within a PopoverRoot');
  }

  return context;
};
